import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';

export const SecuritySettings = () => {
  const { user } = useAuth();
  const { toast } = useToast();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});

  const validatePassword = () => {
    const errors: Record<string, string> = {};
    if (!currentPassword) errors.current = 'Current password is required';
    if (!newPassword) errors.new = 'New password is required';
    else if (newPassword.length < 6) errors.new = 'Password must be at least 6 characters';
    else if (newPassword === currentPassword) errors.new = 'New password must be different from current password';
    if (!confirmPassword) errors.confirm = 'Please confirm your new password';
    else if (newPassword !== confirmPassword) errors.confirm = 'Passwords do not match';
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePassword()) return;
    if (!user?.email) return;
    setIsChangingPassword(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });
      if (signInError) {
        setPasswordErrors({ current: 'Current password is incorrect' });
        return;
      }
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      toast({ title: 'Password updated', description: 'Your password has been changed successfully' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordErrors({});
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Failed to update password', variant: 'destructive' });
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
      <div className="space-y-1.5">
        <Label htmlFor="current-password" className="text-sm">Current password</Label>
        <Input
          id="current-password"
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          placeholder="Enter your current password"
          autoComplete="current-password"
        />
        {passwordErrors.current && <p className="text-xs text-destructive">{passwordErrors.current}</p>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="new-password" className="text-sm">New password</Label>
        <Input
          id="new-password"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="At least 6 characters"
          autoComplete="new-password"
        />
        {passwordErrors.new && <p className="text-xs text-destructive">{passwordErrors.new}</p>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="confirm-password" className="text-sm">Confirm new password</Label>
        <Input
          id="confirm-password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Re-enter the new password"
          autoComplete="new-password"
        />
        {passwordErrors.confirm && <p className="text-xs text-destructive">{passwordErrors.confirm}</p>}
      </div>

      <div className="pt-2">
        <Button type="submit" disabled={isChangingPassword} className="shadow-fluent-2">
          {isChangingPassword ? 'Updating…' : 'Update password'}
        </Button>
      </div>
    </form>
  );
};
